import { Ref, nextTick, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';

import { useResizeObserver, useScroll } from '@vueuse/core';

import { useUserStore } from '@/stores/pinia/useUserStore';

import { useRoute, useRouter } from 'vue-router/composables';
import { parsePageNumber } from '@/helpers/composables/usePagination';

export const TIMELINE_PAGE_ANCHOR = 'timeline_page_anchor' as const;
const TIMELINE_MAX_PAGES = 10;

type TimelineScrollDeps = {
	loading: Ref<boolean>;
	result: Ref<unknown>;
	hasMore: Ref<boolean>;
	fetchMore: () => Promise<boolean>;
};

export function useTimelinePageScroll(el: Ref<HTMLElement | null>, getDeps: () => TimelineScrollDeps) {
	const route = useRoute();
	const router = useRouter();
	const { isLoggedIn } = storeToRefs(useUserStore());

	const currentPage = ref(0);
	const pageBoundaryBuckets = ref<number[]>([0]);
	const positionsByAnchor = ref<number[]>([]);

	function replacePageQueryParam(pageNum: number) {
		if (isLoggedIn.value) return;
		if (route.name !== 'app.editorial.subdomain.home') return;
		if (pageNum > 1) {
			if (route.query.page !== pageNum + '') {
				router.replace({ path: route.path, query: { ...route.query, page: pageNum + '' } }).catch(() => {});
			}
		} else if (route.query.page) {
			const { page: _, ...query } = route.query;
			router.replace({ path: route.path, query }).catch(() => {});
		}
	}

	function updatePageParam(scrollY: number) {
		if (!positionsByAnchor.value.length) return;
		const activePage = positionsByAnchor.value.reduce((page, anchorY, index) => {
			return anchorY <= scrollY ? index + 1 : page;
		}, 1);
		if (activePage !== currentPage.value) {
			currentPage.value = activePage;
			replacePageQueryParam(Math.min(activePage, TIMELINE_MAX_PAGES));
		}
	}

	// On mount scroll to depth when ?page=x is in URL
	onMounted(async () => {
		const scroll = useScroll(document, { throttle: 100 });
		watch(scroll.y, updatePageParam);

		useResizeObserver(el, () => {
			const anchors = el.value?.querySelectorAll(`[data-page="${TIMELINE_PAGE_ANCHOR}"]`);
			if (anchors) {
				positionsByAnchor.value = [...anchors].map(a => a.getBoundingClientRect().top + window.scrollY);
			}
		});

		const { loading, result, hasMore, fetchMore } = getDeps();

		const targetPage = Math.min(parsePageNumber((route.query.page as string) || '1'), TIMELINE_MAX_PAGES);
		if (targetPage <= 1) return;

		if (loading.value || !result.value) {
			await new Promise<void>(resolve => {
				const stop = watch(loading, isLoading => {
					if (!isLoading && result.value) {
						stop();
						resolve();
					}
				});
			});
		}

		for (let i = 1; i < targetPage; i++) {
			if (!hasMore.value) break;
			await fetchMore();
		}

		currentPage.value = targetPage;

		await nextTick();
		const anchors = el.value?.querySelectorAll(`[data-page="${TIMELINE_PAGE_ANCHOR}"]`);
		anchors?.[targetPage - 1]?.scrollIntoView({ behavior: 'instant', block: 'start' });
	});

	return {
		currentPage,
		pageBoundaryBuckets,
	};
}
